/**
 * Consumer paywall (Option A — pay at Get Started, before the fact-find). Only ever shown when
 * billing is live AND the signed-in consumer has no active access. Redirects to Stripe hosted
 * Checkout; on return the app re-checks entitlement and lets them in.
 */
import React, { useState } from 'react';
import { Lock, Check, ArrowRight, ShieldCheck, AlertCircle, LogOut } from 'lucide-react';
import { startCheckout } from '../../services/billingApi.ts';

const INCLUDED = [
  'Your full personalised plan — fees, cashflow, Age Pension and estate outcomes over time',
  'Model it yourself, or let the Suggestion Engine compare strategies for you',
  'Your Statement of Advice and the AI assistant',
  'Everything saved securely — revisit any time during your access period',
];

interface PaywallProps {
  onSignOut?: () => void;
  notice?: React.ReactNode;
}

const Paywall = ({ onSignOut, notice }: PaywallProps) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pay = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await startCheckout('consumer');
      if (!res.ok) {
        setError(res.message || 'Could not start the payment page. Please try again.');
        setBusy(false);
      }
      // On success the browser navigates to Stripe — no further UI needed here.
    } catch {
      setError('We couldn’t reach the payment service. Please try again in a moment.');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5">
        <div className="relative overflow-hidden bg-gradient-to-br from-[#1a6fd4] to-[#17b8e0] px-6 pt-7 pb-8 text-center text-white">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur">
            <Lock className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold">Unlock your full plan</h1>
          <p className="mt-1 text-sm text-white/85">Full access for 4 months — one payment, no auto-renewal</p>
        </div>

        <div className="p-6 space-y-5">
          {notice && (
            <div className="flex items-start gap-2.5 rounded-xl border border-blue-200 bg-white px-4 py-3">
              <AlertCircle size={16} className="mt-0.5 shrink-0 text-blue-600" />
              <p className="text-sm text-blue-800">{notice}</p>
            </div>
          )}

          <div className="text-center">
            <div className="text-3xl font-bold text-slate-900">$480</div>
            <div className="text-sm text-slate-500">for 4 months of full access</div>
          </div>

          <ul className="space-y-2.5">
            {INCLUDED.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-slate-700">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <Check size={13} />
                </span>
                {item}
              </li>
            ))}
          </ul>

          {error && (
            <div className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
              <AlertCircle size={16} className="mt-0.5 shrink-0 text-red-600" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            type="button"
            onClick={pay}
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] py-3 font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:shadow-xl hover:brightness-[1.04] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? 'Taking you to secure payment…' : (<>Pay securely &amp; continue <ArrowRight size={16} /></>)}
          </button>

          <div className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <ShieldCheck size={13} /> Secure payment by Stripe · Your card details never touch our servers
          </div>

          {onSignOut && (
            <button type="button" onClick={onSignOut} className="flex w-full items-center justify-center gap-1.5 text-sm text-slate-500 transition hover:text-slate-700">
              <LogOut size={14} /> Sign out
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Paywall;
